// CurrencyInput — controlled money text input that presents currency formatting
// without disturbing the caller's raw stored value. The caller keeps storing
// the raw interim string (e.g. "85000", or a half-typed "85000.") exactly as
// today — parseMoney/serialisation are untouched. This component only changes
// what is *displayed*: while focused it shows the raw value verbatim so typing,
// decimals and backspace behave naturally; once blurred, a fully-parseable
// value is redisplayed grouped to 2dp (e.g. "85,000.00") so a saved amount
// reads as currency. onChange always reports the raw text upward, never the
// formatted display string, so the interim-typing contract with the form state
// is unchanged. ariaInvalid/ariaDescribedBy forward straight to the input so a
// caller can associate a validation message with this control.
import { useState, type ChangeEvent } from "react";
import { clsx } from "clsx";
import { parseNumericText } from "../../utils/numericText";

export interface CurrencyInputProps {
  id?: string;
  value: string;
  onChange: (raw: string) => void;
  ariaLabel?: string;
  ariaInvalid?: boolean;
  ariaDescribedBy?: string;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  size?: "sm" | "md";
  /** When false, render without the $ adornment — for signed money fields
   *  (e.g. adjustments) where "$-500.00" would misread; the sign stays the
   *  leading character. Defaults to true. */
  showSymbol?: boolean;
}

const GROUPED_FORMATTER = new Intl.NumberFormat("en-AU", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// Blurred display value: a fully-parseable amount — including comma-grouped
// input pasted from SAP/spreadsheets — is grouped to 2dp (no currency symbol;
// the $ is a separate adornment); anything blank, partial, or unparseable
// falls back to the raw text so an in-progress edit is never silently mangled.
function displayValue(value: string): string {
  if (value.trim() === "") return "";
  const n = parseNumericText(value);
  if (n === null) return value;
  return GROUPED_FORMATTER.format(n);
}

export default function CurrencyInput({
  id,
  value,
  onChange,
  ariaLabel,
  ariaInvalid,
  ariaDescribedBy,
  className,
  disabled,
  placeholder,
  size = "md",
  showSymbol = true,
}: CurrencyInputProps) {
  const [focused, setFocused] = useState(false);

  const shown = focused ? value : displayValue(value);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const handleFocus = () => {
    setFocused(true);
  };

  const handleBlur = () => {
    setFocused(false);
  };

  return (
    <div className="relative">
      {showSymbol && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400"
        >
          $
        </span>
      )}
      <input
        id={id}
        type="text"
        inputMode="decimal"
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        className={clsx(
          size === "sm" ? "input-sm" : "input",
          "tabular-nums",
          showSymbol && "pl-7",
          className,
        )}
        value={shown}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={disabled}
        placeholder={placeholder}
      />
    </div>
  );
}
